/**
 * Genera e imprime etiquetas en formato PDF para impresoras Zebra
 * Tamaño: 10cm x 5cm (3.94" x 1.97")
 */

/**
 * Genera HTML para una etiqueta individual optimizada para PDF
 */
function generarEtiquetaPDF(pedido, index) {
  const fechaProduccion = formatearFechaCorta(pedido.fecha);
  const fechaVencimiento = calcularFechaVencimiento(pedido.fecha);

  const sedeNombre = pedido.sede?.nombre || 'Sin sede';
  const sedeCodigo = pedido.sede?.codigo || 'N/A';
  const sectorNombre = pedido.sector?.nombre || mapearSector(pedido.usuario?.tipo);
  const tipoComida = (pedido.tipo_comida || 'almuerzo').toUpperCase();

  const tipoMenu = pedido.items
    .map(item => `${item.plato}`)
    .join(', ');

  return `
    <div class="etiqueta" style="page-break-after: always;">
      <div class="etiqueta-header">
        <strong>Vianda - ${sedeCodigo} [${tipoComida}]</strong>
      </div>

      <div class="etiqueta-nombre">
        ${pedido.usuario.nombre}
      </div>

      <div class="etiqueta-info">
        <div><strong>Sede:</strong> ${sedeNombre}</div>
        <div><strong>Sector:</strong> ${sectorNombre}</div>
      </div>

      <div class="etiqueta-menu">
        <strong>Menú:</strong><br>
        ${tipoMenu}
      </div>

      <div class="etiqueta-fechas">
        <div><strong>Producción:</strong> ${fechaProduccion}</div>
        <div><strong>Vence:</strong> ${fechaVencimiento}</div>
      </div>

      <div class="etiqueta-footer">
        Pedido #${pedido.id}
      </div>
    </div>
  `;
}

/**
 * Genera PDF con todas las etiquetas e inicia impresión
 */
export function imprimirEtiquetasPDF(pedidos) {
  if (pedidos.length === 0) {
    alert('No hay pedidos para imprimir');
    return;
  }

  // Generar HTML de todas las etiquetas
  const etiquetasHTML = pedidos.map((pedido, index) =>
    generarEtiquetaPDF(pedido, index)
  ).join('');

  // Crear ventana de impresión
  const printWindow = window.open('', '_blank');

  if (!printWindow) {
    alert('Por favor, permite ventanas emergentes para imprimir las etiquetas');
    return;
  }

  // Escribir HTML con estilos específicos para etiquetas
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>MealPoint - Etiquetas de Pedidos</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        @page {
          size: 10cm 5cm;
          margin: 0;
        }

        body {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 0;
        }

        .etiqueta {
          width: 10cm;
          height: 5cm;
          padding: 0.4cm;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          border: 1px solid #000;
          page-break-after: always;
        }

        .etiqueta:last-child {
          page-break-after: auto;
        }

        .etiqueta-header {
          font-size: 14pt;
          font-weight: bold;
          text-align: center;
          padding-bottom: 0.2cm;
          border-bottom: 2px solid #000;
          margin-bottom: 0.2cm;
        }

        .etiqueta-nombre {
          font-size: 16pt;
          font-weight: bold;
          margin-bottom: 0.15cm;
        }

        .etiqueta-info {
          font-size: 10pt;
          margin-bottom: 0.15cm;
        }

        .etiqueta-info div {
          margin-bottom: 0.05cm;
        }

        .etiqueta-menu {
          font-size: 10pt;
          line-height: 1.3;
          margin-bottom: 0.2cm;
          flex-grow: 1;
        }

        .etiqueta-menu strong {
          font-size: 9pt;
        }

        .etiqueta-fechas {
          font-size: 11pt;
          margin-bottom: 0.15cm;
        }

        .etiqueta-fechas div {
          margin-bottom: 0.05cm;
        }

        .etiqueta-footer {
          font-size: 9pt;
          color: #666;
          text-align: center;
          padding-top: 0.1cm;
          border-top: 1px solid #ccc;
        }

        @media print {
          body {
            margin: 0;
            padding: 0;
          }

          .etiqueta {
            border: none;
          }
        }
      </style>
    </head>
    <body>
      ${etiquetasHTML}
      <script>
        // Auto-imprimir cuando cargue
        window.onload = function() {
          setTimeout(function() {
            window.print();
          }, 250);
        };

        // Cerrar ventana después de imprimir o cancelar
        window.onafterprint = function() {
          setTimeout(function() {
            window.close();
          }, 100);
        };
      </script>
    </body>
    </html>
  `);

  printWindow.document.close();
}

/**
 * Formatea fecha a formato corto DD/MM/YYYY
 */
function formatearFechaCorta(fechaStr) {
  const [year, month, day] = fechaStr.split('-');
  return `${day}/${month}/${year}`;
}

/**
 * Calcula fecha de vencimiento (día siguiente)
 */
function calcularFechaVencimiento(fechaProduccionStr) {
  const [year, month, day] = fechaProduccionStr.split('-');
  const fecha = new Date(year, month - 1, day);
  fecha.setDate(fecha.getDate() + 1);

  const yearVenc = fecha.getFullYear();
  const monthVenc = String(fecha.getMonth() + 1).padStart(2, '0');
  const dayVenc = String(fecha.getDate()).padStart(2, '0');

  return `${dayVenc}/${monthVenc}/${yearVenc}`;
}

/**
 * Mapea tipo de usuario a nombre de sector
 */
function mapearSector(tipoUsuario) {
  const sectores = {
    'operario': 'Operario/Fábrica',
    'oficina': 'Oficina/Administración',
    'admin': 'Administración'
  };

  return sectores[tipoUsuario] || tipoUsuario;
}

/**
 * Genera PDF para descarga (sin imprimir)
 */
export function descargarEtiquetasPDF(pedidos, nombreArchivo = 'etiquetas') {
  if (pedidos.length === 0) {
    alert('No hay pedidos para descargar');
    return;
  }

  // Similar a imprimirEtiquetasPDF pero sin auto-print
  const etiquetasHTML = pedidos.map((pedido, index) =>
    generarEtiquetaPDF(pedido, index)
  ).join('');

  const printWindow = window.open('', '_blank');

  if (!printWindow) {
    alert('Por favor, permite ventanas emergentes');
    return;
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${nombreArchivo}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        @page {
          size: 10cm 5cm;
          margin: 0;
        }

        body {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 0;
        }

        .etiqueta {
          width: 10cm;
          height: 5cm;
          padding: 0.4cm;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          border: 1px solid #000;
          page-break-after: always;
        }

        .etiqueta:last-child {
          page-break-after: auto;
        }

        .etiqueta-header {
          font-size: 14pt;
          font-weight: bold;
          text-align: center;
          padding-bottom: 0.2cm;
          border-bottom: 2px solid #000;
          margin-bottom: 0.2cm;
        }

        .etiqueta-nombre {
          font-size: 16pt;
          font-weight: bold;
          margin-bottom: 0.15cm;
        }

        .etiqueta-info {
          font-size: 10pt;
          margin-bottom: 0.15cm;
        }

        .etiqueta-info div {
          margin-bottom: 0.05cm;
        }

        .etiqueta-menu {
          font-size: 10pt;
          line-height: 1.3;
          margin-bottom: 0.2cm;
          flex-grow: 1;
        }

        .etiqueta-menu strong {
          font-size: 9pt;
        }

        .etiqueta-fechas {
          font-size: 11pt;
          margin-bottom: 0.15cm;
        }

        .etiqueta-fechas div {
          margin-bottom: 0.05cm;
        }

        .etiqueta-footer {
          font-size: 9pt;
          color: #666;
          text-align: center;
          padding-top: 0.1cm;
          border-top: 1px solid #ccc;
        }

        @media print {
          body {
            margin: 0;
            padding: 0;
          }

          .etiqueta {
            border: none;
          }
        }

        .instrucciones {
          padding: 20px;
          background: #f0f4ff;
          border: 2px solid #27AE60;
          margin: 20px;
          border-radius: 8px;
        }

        .instrucciones h2 {
          color: #27AE60;
          margin-bottom: 10px;
        }

        .instrucciones button {
          background: #27AE60;
          color: white;
          border: none;
          padding: 12px 24px;
          font-size: 16px;
          border-radius: 5px;
          cursor: pointer;
          margin-right: 10px;
        }

        .instrucciones button:hover {
          background: #5568d3;
        }

        @media print {
          .instrucciones {
            display: none;
          }
        }
      </style>
    </head>
    <body>
      <div class="instrucciones">
        <h2>📋 Etiquetas Listas para Imprimir</h2>
        <p style="margin-bottom: 15px;">
          Se generaron ${pedidos.length} etiqueta(s) de 10cm x 5cm.<br>
          Configurá tu impresora Zebra con etiquetas de ese tamaño.
        </p>
        <button onclick="window.print()">🖨️ Imprimir Ahora</button>
        <button onclick="window.close()">❌ Cerrar</button>
      </div>
      ${etiquetasHTML}
    </body>
    </html>
  `);

  printWindow.document.close();
}
